import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import { db } from "./firebase";
import { useAppHeader } from "./ui/AppShell";
import { AsyncState, EmptyState, useAsync } from "./ui/AsyncState";
import { Modal } from "./ui/ConfirmDialog";
import { useToast } from "./ui/Toast";
import { copyToClipboard } from "./utils/clipboard";
import { useOnline } from "./utils/useOnline";
import {
  canOpenExternalLinks,
  googleMapsApiKey,
  isValidLatLng,
  mapLinks,
  navigationOptions,
  toCoordinate,
  type MapApp,
  type MapLinks,
} from "./utils/platform";

/**
 * Mapa střelnice závodu (/mapviewer/:raceId).
 *
 * Fullscreen obrazovka AppShellu (App.tsx): vložená mapa Google (Embed API,
 * klíč z env podle platformy) vyplní plochu mezi hlavičkou a spodní
 * navigací; v hlavičce je „Navigovat" – nabídka odkazu na mapu od správce
 * (jen střelnice bez souřadnic), Apple Map (jen iOS), Map Google a Waze.
 * Starý WebView obal odkazy ven otevřít neumí – nabídka tam odkazy
 * (a souřadnice) zkopíruje. Bez připojení se místo mapy ukáže hláška
 * a po připojení se mapa načte sama.
 * Střelnice se k závodu páruje přes NÁZEV (races.shootingRange ==
 * shootingRanges.name) – změna na id by byla datová migrace mimo redesign.
 * Stavy: načítám / chyba se „Zkusit znovu" / nenalezeno (závod, střelnice,
 * poloha) – během načítání nic z toho nebliká.
 */

/**
 * Plocha mapy mezi hlavičkou a spodní navigací. Leží absolutně v mainu
 * (position: relative) od spodní hrany hlavičky po spodek okna, takže ji
 * neposune nic, co AppShell vykreslí v mainu před obsahem routy (offline
 * pruh): main se neposouvá a ovládání mapy nezajede pod navigaci. Offline
 * pruh tu zůstane schovaný pod plochou (neprůhledné pozadí – v okrajích
 * safe-area by jinak prosvítal). (Čistší řešení – flex sloupec
 * v .lks-main--fullscreen – patří do ui.css.) Fullscreen main nemá spodní
 * ani boční padding: výšku navigace odečítá padding z --lks-nav-real (měří
 * AppShell), boky vrací safe-area (výřez iPhonu na šířku).
 */
const FILL_STYLE: React.CSSProperties = {
  position: "absolute",
  top: "var(--lks-header-real, calc(var(--safe-top, 0px) + var(--lks-header-h, 3.5rem)))",
  right: 0,
  bottom: 0,
  left: 0,
  overflow: "hidden",
  backgroundColor: "var(--lks-bg)",
  paddingBottom: "var(--lks-nav-real, var(--safe-bottom, 0px))",
  paddingLeft: "env(safe-area-inset-left, 0px)",
  paddingRight: "env(safe-area-inset-right, 0px)",
};

/**
 * Stavové obrazovky (načítám, nenalezeno) jako běžná stránka – odsazení
 * nad spodní navigací a od výřezů displeje, které fullscreen main nedává.
 */
const PAGE_STYLE: React.CSSProperties = {
  paddingLeft: "calc(env(safe-area-inset-left, 0px) + var(--lks-gutter, 1rem))",
  paddingRight: "calc(env(safe-area-inset-right, 0px) + var(--lks-gutter, 1rem))",
  paddingBottom:
    "calc(var(--lks-nav-real, var(--safe-bottom, 0px)) + var(--lks-sp-4, 1rem))",
};

const APP_ICONS: Record<MapApp, string> = {
  link: "bi-link-45deg",
  apple: "bi-apple",
  google: "bi-google",
  waze: "bi-car-front",
};

interface MapData {
  /** Závod v databázi není (smazaný, starý odkaz). */
  raceMissing: boolean;
  /** Název střelnice ze závodu ("" = nevyplněná). */
  rangeName: string;
  /** Střelnice s tímto názvem je v kolekci shootingRanges. */
  rangeFound: boolean;
  /** Platné souřadnice střelnice; null = mapu nelze zobrazit. */
  coords: { lat: number; lng: number } | null;
  /**
   * Cíle pro „Navigovat" (souřadnice, jinak text místa a odkaz na mapu);
   * null = není kam.
   */
  links: MapLinks | null;
}

const RACE_MISSING: MapData = {
  raceMissing: true,
  rangeName: "",
  rangeFound: false,
  coords: null,
  links: null,
};

const asText = (value: unknown): string =>
  typeof value === "string" ? value : "";

/** Závod → střelnice (podle názvu) → souřadnice a odkazy pro navigaci. */
const loadMapData = async (raceId: string): Promise<MapData> => {
  const raceSnap = await getDoc(doc(db, "races", raceId));
  if (!raceSnap.exists()) return RACE_MISSING;

  const race = raceSnap.data();
  const rangeName = asText(race.shootingRange);
  // mapUrl závodu = místo (text nebo odkaz na mapu) zkopírované ze střelnice
  // při založení závodu; pro navigaci se použije, jen když střelnici podle
  // názvu dohledat nejde.
  const raceQuery = asText(race.mapUrl);

  if (!rangeName.trim()) {
    return {
      raceMissing: false,
      rangeName: "",
      rangeFound: false,
      coords: null,
      links: mapLinks({ query: raceQuery }),
    };
  }

  // Párování přes název beze změny. Bez orderBy vrací Firestore shody podle
  // id dokumentu, limit(1) je tedy totéž jako dřívější snapshot.docs[0].
  const snapshot = await getDocs(
    query(
      collection(db, "shootingRanges"),
      where("name", "==", rangeName),
      limit(1)
    )
  );
  if (snapshot.empty) {
    return {
      raceMissing: false,
      rangeName,
      rangeFound: false,
      coords: null,
      links: mapLinks({ query: raceQuery, name: rangeName }),
    };
  }

  const range = snapshot.docs[0].data();
  const lat = toCoordinate(range.lat);
  const lng = toCoordinate(range.lng);
  return {
    raceMissing: false,
    rangeName,
    rangeFound: true,
    coords:
      lat !== null && lng !== null && isValidLatLng(lat, lng)
        ? { lat, lng }
        : null,
    // mapLink je text z našeptávače (backlog #30), nebo ručně zadaný odkaz
    // na mapu – použije se jen pro navigaci bez souřadnic; se souřadnicemi
    // se naviguje přesně na bod.
    links: mapLinks({ lat, lng, query: asText(range.mapLink), name: rangeName }),
  };
};

const MapViewer: React.FC = () => {
  const { raceId } = useParams<{ raceId: string }>();
  const navigate = useNavigate();
  const { setHeader } = useAppHeader();
  const { showToast } = useToast();
  const [navOpen, setNavOpen] = useState(false);
  const online = useOnline();
  // Starý obal: odkazy ven neotevře, nabídka je zkopíruje
  const [canOpen] = useState(() => canOpenExternalLinks());

  const page = useAsync<MapData>(
    () => (raceId ? loadMapData(raceId) : Promise.resolve(RACE_MISSING)),
    [raceId]
  );
  const data = page.status === "ready" ? page.data : null;
  const rangeName = data ? data.rangeName : "";
  const links = data ? data.links : null;
  const hasLinks = links !== null;

  const openNav = useCallback(() => setNavOpen(true), []);
  const closeNav = useCallback(() => setNavOpen(false), []);
  // Zavřít až po kliknutí: React překreslí v mikroúloze ještě před výchozí
  // akcí odkazu a odkaz odpojený z DOM by se vůbec neotevřel.
  const closeNavAfterClick = () => {
    window.setTimeout(closeNav, 0);
  };

  // „Navigovat" v hlavičce – jen když je kam (souřadnice, text místa nebo
  // odkaz na mapu).
  const actions = useMemo(
    () =>
      hasLinks ? (
        <button
          type="button"
          className="lks-header__btn"
          aria-haspopup="dialog"
          aria-label="Navigovat"
          onClick={openNav}
        >
          <i className="bi bi-sign-turn-right" aria-hidden="true" />
          {/* Na úzkém displeji jen ikona jako u Zpět (ui.css, 23,5em). */}
          <span className="lks-header__btn-text">Navigovat</span>
        </button>
      ) : undefined,
    [hasLinks, openNav]
  );

  useLayoutEffect(() => {
    setHeader({
      title: "Mapa střelnice",
      subtitle: rangeName || undefined,
      back: "history",
      actions,
    });
  }, [setHeader, rangeName, actions]);

  // Stejně jako Zpět v hlavičce: historií, při přímém otevření na přehled.
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/user");
  };

  const options = links ? navigationOptions(links) : [];
  let navText: string;
  if (data && data.coords) {
    navText = "Mapová aplikace se otevře s trasou na střelnici.";
  } else if (links && links.link && links.apps) {
    navText =
      "Střelnice nemá v aplikaci přesnou polohu – otevřete zadaný odkaz na mapu, nebo ji mapová aplikace vyhledá podle názvu místa.";
  } else if (links && links.link) {
    navText =
      "Střelnice nemá v aplikaci přesnou polohu – otevře se zadaný odkaz na mapu.";
  } else {
    navText =
      "Střelnice nemá v aplikaci přesnou polohu – mapová aplikace ji vyhledá podle názvu místa.";
  }
  /** Zkopírování ve starém obalu (toast s výsledkem), pak zavřít nabídku. */
  const copyAndClose = async (text: string, success: string) => {
    const ok = await copyToClipboard(text);
    showToast(ok ? success : `Kopírování se nepodařilo: ${text}`, ok ? "success" : "warning");
    closeNav();
  };
  const coordsText = data && data.coords
    ? `${data.coords.lat.toFixed(6)}, ${data.coords.lng.toFixed(6)}`
    : "";

  const navModal = (
    <Modal
      open={navOpen && options.length > 0}
      onClose={closeNav}
      title="Navigovat na střelnici"
      size="sm"
    >
      {rangeName ? <p className="fw-semibold mb-1">{rangeName}</p> : null}
      <p className="text-body-secondary small mb-3">
        {canOpen
          ? navText
          : "Tato verze aplikace LKS neumí otevírat odkazy mimo aplikaci – zkopírujte si polohu nebo odkaz a vložte je do mapové aplikace či prohlížeče."}
      </p>
      <div className="list-group">
        {!canOpen && coordsText ? (
          <button
            type="button"
            className="list-group-item list-group-item-action lks-actionlist__item"
            onClick={() =>
              copyAndClose(coordsText, "Souřadnice zkopírovány – vložte je do mapové aplikace.")
            }
          >
            <i className="bi bi-geo-alt lks-actionlist__icon" aria-hidden="true" />
            <span className="lks-actionlist__text">
              <span className="lks-actionlist__label">Kopírovat souřadnice</span>
              <span className="lks-actionlist__desc">{coordsText}</span>
            </span>
            <i className="bi bi-clipboard lks-actionlist__chevron" aria-hidden="true" />
          </button>
        ) : null}
        {options.map((option) =>
          canOpen ? (
            <a
              key={option.app}
              href={option.url}
              target="_blank"
              rel="noopener noreferrer"
              className="list-group-item list-group-item-action lks-actionlist__item"
              onClick={closeNavAfterClick}
            >
              <i
                className={`bi ${APP_ICONS[option.app]} lks-actionlist__icon`}
                aria-hidden="true"
              />
              <span className="lks-actionlist__text">
                <span className="lks-actionlist__label">{option.label}</span>
                {option.description ? (
                  <span className="lks-actionlist__desc text-break">
                    {option.description}
                  </span>
                ) : null}
              </span>
              <i
                className="bi bi-box-arrow-up-right lks-actionlist__chevron"
                aria-hidden="true"
              />
            </a>
          ) : (
            <button
              key={option.app}
              type="button"
              className="list-group-item list-group-item-action lks-actionlist__item"
              onClick={() =>
                copyAndClose(
                  option.url,
                  "Odkaz zkopírován – vložte ho do prohlížeče nebo mapové aplikace."
                )
              }
            >
              <i
                className={`bi ${APP_ICONS[option.app]} lks-actionlist__icon`}
                aria-hidden="true"
              />
              <span className="lks-actionlist__text">
                <span className="lks-actionlist__label">{option.label} – zkopírovat odkaz</span>
                {option.description ? (
                  <span className="lks-actionlist__desc text-break">
                    {option.description}
                  </span>
                ) : null}
              </span>
              <i className="bi bi-clipboard lks-actionlist__chevron" aria-hidden="true" />
            </button>
          )
        )}
      </div>
    </Modal>
  );

  const apiKey = googleMapsApiKey();

  // Bez připojení by vložená mapa ukázala chybovou stránku prohlížeče a po
  // návratu signálu by se sama neobnovila (offline pruh je pod plochou).
  // Hláška místo ní; po připojení se iframe vytvoří znovu a mapa načte.
  if (data && data.coords && apiKey && !online) {
    return (
      <div className="lks-page lks-page--user" style={PAGE_STYLE}>
        <EmptyState
          icon="bi-wifi-off"
          title="Jste offline"
          text="Mapa se načte, jakmile bude telefon zase online."
          action={hasLinks ? { label: "Navigovat", onClick: openNav } : undefined}
        />
        {navModal}
      </div>
    );
  }

  if (data && data.coords && apiKey) {
    const { lat, lng } = data.coords;
    // Režim place = mapa se značkou na střelnici (view ukazoval jen výřez).
    const src = `https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(
      apiKey
    )}&q=${lat},${lng}&zoom=15`;
    return (
      <div style={FILL_STYLE}>
        <iframe
          title={rangeName ? `Mapa střelnice ${rangeName}` : "Mapa střelnice"}
          src={src}
          style={{ display: "block", width: "100%", height: "100%", border: 0 }}
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
        />
        {navModal}
      </div>
    );
  }

  // Nenalezené stavy: když jde aspoň navigovat (text místa, odkaz na mapu),
  // hlavní akcí je Navigovat přímo ve stavu – popisek tlačítka v hlavičce
  // je na úzkém displeji skrytý, text se na něj proto neodkazuje. Jinak Zpět.
  const back = { label: "Zpět", onClick: goBack };
  const stateAction = hasLinks ? { label: "Navigovat", onClick: openNav } : back;
  const navHint = hasLinks ? " Místo můžete otevřít v mapové aplikaci." : "";

  let content: React.ReactNode = null;
  if (!data || data.raceMissing) {
    content = (
      <EmptyState
        icon="bi-calendar-x"
        title="Závod nenalezen"
        text="Závod byl zrušen nebo odkaz už neplatí."
        action={back}
      />
    );
  } else if (!data.rangeName.trim()) {
    content = (
      <EmptyState
        icon="bi-geo"
        title="Střelnice není vyplněná"
        text={`U závodu zatím není zadaná střelnice.${navHint}`}
        action={stateAction}
      />
    );
  } else if (!data.rangeFound) {
    content = (
      <EmptyState
        icon="bi-geo"
        title="Střelnice nenalezena"
        text={`Střelnice „${data.rangeName}“ není v seznamu střelnic.${navHint}`}
        action={stateAction}
      />
    );
  } else if (!data.coords) {
    content = (
      <EmptyState
        icon="bi-geo-alt"
        title="Mapa není k dispozici"
        text={`Střelnice „${data.rangeName}“ nemá v aplikaci zadanou polohu.${navHint}`}
        action={stateAction}
      />
    );
  } else {
    // Souřadnice jsou, chybí klíč Google Maps (env při buildu) – mapa ne,
    // navigace do mapových aplikací ano.
    content = (
      <EmptyState
        icon="bi-map"
        title="Mapu teď nelze zobrazit"
        text="Trasu na střelnici otevřete v mapové aplikaci."
        action={{ label: "Navigovat", onClick: openNav }}
      />
    );
  }

  return (
    <div className="lks-page lks-page--user" style={PAGE_STYLE}>
      <AsyncState
        status={page.status}
        error={page.error}
        onRetry={page.reload}
        loadingText="Načítám mapu…"
      >
        {content}
      </AsyncState>
      {navModal}
    </div>
  );
};

export default MapViewer;
